import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight, Rocket, ArrowUpRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { stats, projects } from '../data/mock';
import CuttingEdgeSolutions from '../components/ui/CuttingEdgeSolutions';
import ScrollReveal, { StaggerContainer, StaggerItem } from '../components/ui/ScrollReveal';
import AnimatedCounter from '../components/ui/AnimatedCounter';
import TypewriterCycle from '../components/ui/TypewriterCycle';
import FloatingCodeCards from '../components/ui/FloatingCodeCards';

const HomePage = () => {
  const [activeTab, setActiveTab] = useState('All');

  const featuredProjects = projects.slice(0, 3);

  return (
    <div className="bg-[#0a0a0a] min-h-screen font-sans selection:bg-cyan-500/30 selection:text-cyan-400">

      {/* 1. HERO SECTION */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Background Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px] opacity-50" />

        {/* Glow Effects */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[100px] animate-pulse" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[100px]" />

        <div className="container mx-auto px-6 relative z-10 grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text Content with Cycling Typewriter Animation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-left"
          >
            {/* Cycling Typewriter Titles */}
            <TypewriterCycle />

            {/* Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="flex flex-wrap gap-4 mt-8"
            >
              <Link to="/contact">
                <Button className="bg-cyan-400 hover:bg-cyan-300 text-black font-bold px-8 py-6 rounded-none skew-x-[-12deg] transition-all hover:scale-105 shadow-[0_0_20px_rgba(0,245,255,0.4)]">
                  <span className="skew-x-[12deg] flex items-center gap-2">
                    Start a Project <ArrowRight size={18} />
                  </span>
                </Button>
              </Link>
              <Link to="/projects">
                <Button variant="outline" className="border-slate-700 hover:border-cyan-400 text-white hover:text-cyan-400 px-8 py-6 rounded-none skew-x-[-12deg] bg-transparent transition-all">
                  <span className="skew-x-[12deg]">View Work</span>
                </Button>
              </Link>
            </motion.div>
          </motion.div>

          {/* Right: Floating Code Visuals with Live Typing Animation */}
          <FloatingCodeCards />
        </div>
      </section>

      {/* 2. STATS SECTION - with scroll-velocity based left-to-right animation */}
      <AnimatedCounter stats={stats} />

      {/* 3. CUTTING EDGE SOLUTIONS SECTION */}
      <ScrollReveal animation="fadeUp" duration={0.8}>
        <CuttingEdgeSolutions />
      </ScrollReveal>

      {/* 4. WORK SECTION - with scroll reveal */}
      <section className="py-32 bg-[#0d0d0f] relative border-t border-white/5">
        <div className="container mx-auto px-6">
          <ScrollReveal animation="fadeUp" className="flex flex-col md:flex-row justify-between items-end mb-16 px-4">
            <div>
              <div className="text-cyan-400 font-mono text-sm mb-2">/ SELECTED WORK</div>
              <h2 className="text-4xl md:text-5xl font-bold text-white font-mono">
                Recent Projects
              </h2>
            </div>
            <Link to="/projects">
              <Button variant="ghost" className="text-white hover:text-cyan-400 group">
                View All Projects <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </ScrollReveal>

          <StaggerContainer staggerDelay={0.2} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProjects.map((project, index) => (
              <StaggerItem key={project.id} animation="fadeUp">
                <div className="group relative rounded-xl overflow-hidden border border-white/10 bg-[#161618] hover:border-cyan-500/50 transition-all duration-500">
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <Button className="rounded-full bg-cyan-400 text-black hover:bg-cyan-300">View Project</Button>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="text-cyan-400 text-xs font-mono mb-2">{project.type}</div>
                    <h3 className="text-xl font-bold text-white mb-2">{project.title}</h3>
                    <p className="text-slate-400 text-sm line-clamp-2">{project.description}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* 5. CTA SECTION - with scroll reveal */}
      <ScrollReveal animation="scaleUp" duration={0.8}>
        <section className="py-32 relative overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-[800px] h-[800px] bg-cyan-900/20 rounded-full blur-[120px]" />
          </div>

          <div className="container mx-auto px-6 relative z-10 text-center">
            <ScrollReveal animation="fadeDown" delay={0.2}>
              <h2 className="text-4xl md:text-6xl font-bold text-white font-mono mb-8">
                Ready to <span className="text-cyan-400">Innovate?</span>
              </h2>
            </ScrollReveal>
            <ScrollReveal animation="fadeUp" delay={0.4}>
              <p className="text-slate-400 text-xl max-w-2xl mx-auto mb-10">
                Let's turn your vision into a game-changing digital product.
              </p>
            </ScrollReveal>
            <ScrollReveal animation="scaleUp" delay={0.6}>
              <Link to="/contact">
                <Button size="lg" className="bg-cyan-400 hover:bg-cyan-300 text-black font-bold h-16 px-10 rounded-full shadow-[0_0_30px_rgba(0,245,255,0.3)] hover:shadow-[0_0_50px_rgba(0,245,255,0.5)] transition-all scale-100 hover:scale-105">
                  Start Your Project
                </Button>
              </Link>
            </ScrollReveal>
          </div>
        </section>
      </ScrollReveal>

    </div>
  );
};

export default HomePage;
